import classNames from 'classnames';
import { memo, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
    getServiceDetails,
    IAppointment,
    insertAuditTrail,
    IService,
    IServiceDetail,
    IXtimeMenuType,
    IXtimeServiceBase,
    LocalizedService,
    LocalizedTransportationOption,
    MaintenanceMenu,
    RNDService,
    useLocalizedServiceFactory,
    useTransportationOptions,
} from '../../../API/GoMoto';
import { Events, DMSType, TransportSubtype } from '../../../API/Enums';
import {
    useAppointmentState,
    useAppointmentUpdateAndPersist,
    useMaskPhone,
    useMaskEmail,
    useMaskVin,
    AppointmentDispatchTypes,
    useDisclaimerInput,
    useAppointmentDispatch,
} from '../../../context/Appointment/appointment';
import { IComment, Types, useCart, useCartDispatch } from '../../../context/cart';
import { useKioskTimeTable, useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../context/Page/page';
import { GMCapitalize } from '../../../controls/Case';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { CTAButton } from '../../../controls/ButtonBase';
import { Disclaimer } from '../../../controls/Disclaimer/Disclaimer';
import { GMInfoGrid, GMInfoGridColumn, GMInfoGridRow } from '../../../controls/InfoGrid';
import { KioskDisclaimer } from '../../../controls/KioskDisclaimer';
import { IServiceItemProps, ServiceItem } from '../../../controls/ServiceItemList/ServiceItem';
import { ServiceItemList } from '../../../controls/ServiceItemList/ServiceItemList';
import { Spacer } from '../../../controls/Spacer/Spacer';
import { useFormatAppointmentDate } from '../../../helpers/Appointment';
import { formatAsPhoneNumber } from '../../../helpers/Number';
import { maskValue } from '../../../helpers/maskValue';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import css from './Summary.module.scss';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import { minEstCost, rndMinEstCost } from '../../../helpers/minEstCost';
import { TermsAndConditions } from '../TermsAndConditions/TermsAndConditions';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { SignatureComponent } from '../../../controls/SignatureComponent/SignatureComponent';
import { textCapitalize } from '../../../helpers/textCapitalize';
import { useDisplayLocalizedVehicle } from '../../../helpers/displayVehicle';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { AtomicRange, SummaryProps } from './Summary.types';
import { LocalizationType } from '../../../context/Localization/localization.types';
import { GMTitle } from '../../../controls/Title';

/**
 * Displays a summary of the customer's appointment with their personal information, vehicle, appointment time, services they requested,
 * comments they added, and a prompt to sign and complete their check-in.
 *
 * @param onProceed - {@link SummaryProps.onProceed onProceed} description.
 */
export const Summary: GoMotoComponent<SummaryProps> = memo(({
    onProceed,
}) => {
    useHideKioskBackground()();
    const Localization: LocalizationType = useLocalization();
    const maskPhone: boolean = useMaskPhone();
    const maskEmail: boolean = useMaskEmail();
    const maskVin: boolean = useMaskVin();
    const setAppointment = useAppointmentUpdateAndPersist();
    const {
        key: kioskKey,
        dealer: {
            name,
            facilityId,
            key: dealerKey,
            showDmsServiceLineEstimates,
            vinRequired,
            tagNumberRequired,
            dmsType,
            consentToSignElectronicallyEnabled,
            roBackerEnabled,
            roBackerAcknowledgmentRequired,
            transportationPageEnabled,
            workAuthorizationEnabled,
            theme,
        },
        kioskType,
    } = useKioskState();
    const formatAppointmentDate = useFormatAppointmentDate();
    const { computeNextAvailableCheckinTime, getIsAppointmentTimeAfterOffset } = useKioskTimeTable();
    const originalAppointment: IAppointment = useAppointmentState();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const cart = useCart();
    const cartDispatch = useCartDispatch();
    const serviceFactory = useLocalizedServiceFactory();
    const { transportationOptions } = useTransportationOptions(kioskKey, dealerKey);
    const { gaveConsentToSignElectronically, termsAcknowledged } = useDisclaimerInput();
    const appointmentDispatch = useAppointmentDispatch();

    const appointment: IAppointment = {
        ...originalAppointment,
        appointmentDateTime:
            getIsAppointmentTimeAfterOffset(originalAppointment.appointmentDateTime)
                ? computeNextAvailableCheckinTime()
                    .toISOString()
                : originalAppointment.appointmentDateTime,
    };

    const [signatureRequired, setSignatureRequired] = useState<boolean>(false);
    const [processing, setProcessing] = useState<boolean>(false);
    const [serviceItems, setServiceItems] = useState<Partial<IServiceItemProps>[]>([]);
    const [appointmentDetails, setAppointmentDetails] = useState<IServiceDetail[]>([]);
    const screenshotRef = useRef<HTMLDivElement>(null);
    const [signatureIsEmpty, setSignatureIsEmpty] = useState<boolean>(true);

    const apptDate: string = formatAppointmentDate(appointment);
    const isKiosk: boolean = isElectron();
    const reduceInfoFontSize: boolean = theme === 'audi' && !isKiosk;

    /**
     * Selector identifying receipt sections that must not be split across
     * PDF pages. Used by the canvas-slice page-break logic in
     * `buildReceiptPdfBlob` to find atomic elements whose layout
     * boundaries the page break must respect.
     */
    const atomicSelector: string = [
        '[data-print-section]',
        '[class*="infoGridRow"]',
        '[class*="serviceItem"]',
        '[class*="signature"]',
        '[class*="disclaimer"]',
        '[class*="Disclaimer"]',
    ].join(', ');

    useEffect(() => {
        if (appointmentDetails.length === 0 && appointment.dmsAppointmentKey) {
            getServiceDetails(kioskKey, dealerKey, appointment.dmsAppointmentKey, appointment.key)
                .then((details) => {
                    setAppointmentDetails(details);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kioskKey, dealerKey, appointment.dmsAppointmentKey, appointment.key]);

    useEffect(() => {
        const items: Partial<IServiceItemProps>[] = [];
        if (!!appointmentDetails.length) {
            appointmentDetails.forEach((detail: IServiceDetail) => {
                items.push({
                    title: detail.opCodeDescription,
                    details: detail.description,
                    price: (showDmsServiceLineEstimates && detail.price) || undefined,
                    minEstCost: minEstCost({
                        useSpgPricing: false,
                        price: detail.price,
                    }) ?? undefined,
                    comment: detail.comment,
                    partsPrice: detail.partsPrice,
                    laborPrice: (dmsType !== DMSType.DEALERTRACK && detail.laborPrice) || undefined,
                    childServices: detail.childServices,
                });
            });
        }
        cart.services
            .filter((service: IService) => service.isTransport !== true)
            .map(serviceFactory)
            .forEach((localizedService: LocalizedService) => {
                items.push({
                    title: localizedService.localization.name,
                    price: showDmsServiceLineEstimates ? (
                        <SpgPriceText
                            price={localizedService.price}
                            priceLow={localizedService.priceLow}
                            priceHigh={localizedService.priceHigh}
                            spgPricing={localizedService.spgPricing}
                            useSpgPricingLogic={!!localizedService.useSpgPricing}
                        />
                    ) : undefined,
                    minEstCost: minEstCost(localizedService) ?? undefined,
                    details: localizedService.localization.description,
                    comment: localizedService.comment,
                    onDelete: () => {
                        cartDispatch({
                            type: Types.RemoveService,
                            payload: localizedService.full,
                        });
                    },
                });
            });
        cart.maintMenus.forEach((maintenanceMenu: MaintenanceMenu) => {
            items.push({
                title: maintenanceMenu.title,
                price: showDmsServiceLineEstimates ? (
                    <SpgPriceText
                        priceLow={maintenanceMenu.priceLow}
                        priceHigh={maintenanceMenu.priceHigh}
                        spgPricing={maintenanceMenu.spgPricing}
                        showSingleWhenNoRange={false}
                    />
                ) : undefined,
                minEstCost: minEstCost(maintenanceMenu) ?? undefined,
                details: maintenanceMenu.opDescriptions.join('\n'),
                onDelete: () => {
                    cartDispatch({
                        type: Types.RemoveMaintMenu,
                        payload: maintenanceMenu,
                    });
                },
            });
        });
        cart.xtime.services?.forEach((xTimeService: IXtimeServiceBase) => {
            items.push({
                title: xTimeService.name,
                price: showDmsServiceLineEstimates ? xTimeService.price : undefined,
                minEstCost: xTimeService.price,
                details: xTimeService.description,
                comment: xTimeService.comment,
                onDelete: () => {
                    cartDispatch({
                        type: Types.RemoveXtimeService,
                        payload: {
                            id: xTimeService.id,
                            type: xTimeService.type,
                        },
                    });
                },
            });
        });
        cart.xtime.menus?.forEach((xTimeMenu: IXtimeMenuType) => {
            items.push({
                title: xTimeMenu.name,
                price: showDmsServiceLineEstimates ? xTimeMenu.totalPrice : undefined,
                minEstCost: xTimeMenu.totalPrice,
                onDelete: () => {
                    cartDispatch({
                        type: Types.RemoveXtimeMenu,
                        payload: { id: xTimeMenu.id },
                    });
                },
            });
        });
        cart.rndServices.forEach((rndService: RNDService) => {
            if (!rndService.doneElsewhere) {
                const localizedService: LocalizedService | undefined = rndService.linkedService ? serviceFactory(rndService.linkedService) : undefined;

                items.push({
                    title: localizedService && localizedService.localization.name ? localizedService.localization.name : rndService.description,
                    price: showDmsServiceLineEstimates ? (
                        <SpgPriceText
                            price={rndService.price ?? localizedService?.price}
                            priceLow={localizedService?.priceLow}
                            priceHigh={localizedService?.priceHigh}
                            spgPricing={localizedService?.useSpgPricing ? localizedService?.spgPricing : undefined}
                        />
                    ) : undefined,
                    minEstCost: rndMinEstCost(rndService) ?? undefined,
                    details: localizedService ? localizedService.localization.description : undefined,
                    onDelete: () => {
                        cartDispatch({
                            type: Types.RemoveRNDService,
                            payload: rndService,
                        });
                    },
                });
            }
        });
        setServiceItems(items);
    }, [appointmentDetails, cart, cartDispatch, serviceFactory, showDmsServiceLineEstimates, dmsType, Localization.AskUsAboutPricing]);

    let transportName: LocalizedTransportationOption['name'] = '';
    if (transportationPageEnabled && cart.transportation && transportationOptions) {
        const selected = transportationOptions.find((transportOption: LocalizedTransportationOption) => {
            return transportOption.type === cart.transportation.type && transportOption.subtype === (cart.transportation.subtype || TransportSubtype.none);
        });
        transportName = selected?.name || '';
    }

    const renderServices: JSX.Element[] = serviceItems.map((service, index) => (
        <ServiceItem
            key={`summary-service-item-${index}`}
            minEstCost={service.minEstCost}
            {...(service as IServiceItemProps)}
        />
    ));

    /**
     * Slice the rendered receipt canvas into a Letter-portrait PDF, pushing
     * any "atomic" element (a service row, an info-grid row, the signature
     * block, a disclaimer block) to the next page rather than splitting it
     * across the page boundary. The PDF wraps each page as an image, so the
     * font + theme appearance is fixed at capture time and survives any
     * downstream printer or viewer without external dependencies — meeting
     * the legal-receipt immutability requirement.
     *
     * `atomicRanges` are top/bottom Y coordinates in the captured canvas's
     * pixel space, measured by the caller against the html2canvas clone
     * (which is forced to 1080px wide and detached from the live scroll
     * container). Measuring there — rather than against the live, scrolled,
     * possibly-mobile-width DOM — is what keeps the straddler check aligned
     * with what was actually drawn into the canvas.
     */
    const buildReceiptPdfBlob: GoMotoFunction<[HTMLCanvasElement, AtomicRange[]], Blob> = (canvas: HTMLCanvasElement, atomicRanges: AtomicRange[]) => {
        /**
         * If the candidate page-end Y falls inside any atomic element, the
         * straddler is returned so the caller can push the page break to
         * that element's top (i.e. start the atomic on a fresh page).
         */
        const findStraddler: GoMotoFunction<[number], AtomicRange | undefined> = (y) => {
            for (const r of atomicRanges) {
                if (y > r.top && y < r.bottom) {
                    return r;
                }
            }
            return undefined;
        };

        const pdf: jsPDF = new jsPDF({
            unit: 'in',
            format: 'letter',
            orientation: 'portrait',
            compress: true,
        });
        const pageWidth: number = 8.5;
        const pageHeight: number = 11;
        const margin: number = 0.5;
        const usableWidth: number = pageWidth - 2 * margin;
        const usableHeight: number = pageHeight - 2 * margin;
        const canvasPxPerInch: number = canvas.width / usableWidth;
        const maxPagePxHeight: number = Math.floor(usableHeight * canvasPxPerInch);

        let pageStart: number = 0;
        let firstPage: boolean = true;

        while (pageStart < canvas.height) {
            const candidateEnd: number = Math.min(pageStart + maxPagePxHeight, canvas.height);
            const straddler: AtomicRange | undefined = findStraddler(candidateEnd);
            /*
             * Default: end the page exactly where its content fits. If an
             * atomic straddles the candidate boundary, push it to the next
             * page instead — unless the atomic is itself bigger than a full
             * page, in which case we have no choice and split it.
             */
            let pageEnd: number = candidateEnd;
            if (straddler && straddler.top > pageStart) {
                pageEnd = straddler.top;
            }
            const sliceHeight: number = pageEnd - pageStart;
            if (sliceHeight <= 0) {
                break;
            }

            const sliceCanvas: HTMLCanvasElement = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sliceHeight;
            const ctx: CanvasRenderingContext2D | null = sliceCanvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(canvas, 0, pageStart, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
            }

            if (!firstPage) {
                pdf.addPage();
            }
            const sliceHeightInches: number = sliceHeight / canvasPxPerInch;
            pdf.addImage(
                sliceCanvas.toDataURL('image/png'),
                'PNG',
                margin,
                margin,
                usableWidth,
                sliceHeightInches,
            );

            firstPage = false;
            pageStart = pageEnd;
        }

        return pdf.output('blob');
    };

    const blobToBase64: GoMotoFunction<[Blob], Promise<string>> = (blob: Blob) =>
        new Promise<string>((resolve: (value: string) => void, reject: (reason?: unknown) => void) => {
            const reader: FileReader = new FileReader();
            reader.onloadend = () => {
                const result: string = typeof reader.result === 'string' ? reader.result : '';
                resolve(result.replace(/^data:application\/pdf;base64,/, ''));
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
        });

    const onComplete: GoMotoFunction<[void], Promise<void>> = async () => {
        if (signatureIsEmpty && ((consentToSignElectronicallyEnabled && gaveConsentToSignElectronically) || !consentToSignElectronicallyEnabled)) {
            setSignatureRequired(true);
        }
        else {
            setSignatureRequired(false);
            /**
             * Populated inside `onclone` with each atomic section's
             * top/bottom Y in the captured canvas's pixel space. We measure
             * there — not on the live DOM — because the live element is
             * inside an `overflow-y: auto` container that the user has
             * almost certainly scrolled (so viewport-relative rects are
             * offset by `scrollTop`) and is at viewport width (which may
             * be mobile, while the canvas is rendered at the clone's
             * forced 1080px width — responsive layouts don't translate
             * uniformly between the two). The clone is fixed-positioned at
             * top:0 with width 1080px, so its rects map 1:1 to canvas
             * pixels at `scale: 1`.
             */
            const atomicRanges: AtomicRange[] = [];
            const image: HTMLCanvasElement | null =
                screenshotRef.current &&
                (await html2canvas(screenshotRef.current, {
                    /**
                     * html2canvas only captures the content that is displayed,
                     * not any overflowed content hidden by a scrollbar. We need to ensure
                     * the canvas size is set to the full content of the div we
                     * want to capture. These changes do not modify the original
                     * DOM
                     */
                    onclone: (_documentClone: Document, screenshotEl: HTMLElement) => {
                        screenshotEl.style.width = '1080px';
                        screenshotEl.style.position = 'fixed';
                        screenshotEl.style.left = '0';
                        screenshotEl.style.top = '0';
                        if (maskPhone) {
                            const phoneEl = screenshotEl.querySelector('[data-screenshot="phone"]');
                            if (phoneEl) {
                                phoneEl.textContent = formatAsPhoneNumber(appointment.phone);
                            }
                        }
                        if (maskEmail) {
                            const emailEl = screenshotEl.querySelector('[data-screenshot="email"]');
                            if (emailEl) {
                                emailEl.textContent = appointment.email;
                            }
                        }

                        /**
                         * Measure atomic ranges against the freshly laid-out
                         * clone. `getBoundingClientRect` forces layout, so
                         * the rects reflect the 1080px-wide layout that
                         * html2canvas is about to paint into the canvas.
                         */
                        const cloneRoot: DOMRect = screenshotEl.getBoundingClientRect();
                        screenshotEl.querySelectorAll<HTMLElement>(atomicSelector)
                            .forEach((el: HTMLElement) => {
                                const r: DOMRect = el.getBoundingClientRect();
                                atomicRanges.push({
                                    top: Math.round(r.top - cloneRoot.top),
                                    bottom: Math.round(r.bottom - cloneRoot.top),
                                });
                            });
                    },
                    scale: 1,
                    scrollY: 0,
                    ignoreElements: (element: Element) =>
                        (/\bAccessibilityButton\w+/).test(element.className) ||
                        (/noSignatureBox/).test(element.className) ||
                        (/applySignature/).test(element.className) ||
                        (/clear/).test(element.className) ||
                        (/remove/).test(element.className),
                }));

            /**
             * Generate the legal-receipt PDF by slicing the rendered receipt
             * canvas (already produced above for `signatureImage`) into Letter
             * pages with section-aware pagination. Same canvas, no second
             * html2canvas call. The PDF wraps page-sized images, so the rendered
             * fonts and theme are baked in as pixels — no fonts ever need to
             * leave the kiosk, and the document looks identical on every device
             * that opens it.
             */
            let receiptPdf: string = '';
            if (image) {
                try {
                    const pdfBlob: Blob = buildReceiptPdfBlob(image, atomicRanges);
                    receiptPdf = await blobToBase64(pdfBlob);
                }
                catch (e) {
                    console.error('Receipt PDF generation failed', e);
                }
            }

            await setAppointment(
                {
                    ...appointment,
                    kioskKey,
                    status: 'scheduled',
                    signatureImage: image?.toDataURL()
                        .replace(/^data:image\/\w+;base64,/, ''),
                    receiptPdf,
                    allowManualComplete: kioskType === 'outdoor' || (!isKiosk && kioskType === 'express'),
                    customerCompletedCheckIn: true,
                    gaveConsentToSign: gaveConsentToSignElectronically,
                    preCheckInData: undefined,
                },
                false,
                false,
                false,
                true,
                undefined,
                true,
                false,
                true,
            );

            await insertAuditTrail(appointment.key, Events.Check_In_Complete);
            onProceed();
        }
    };

    const comments: IComment[] = cart.comments.filter((comment: IComment) => comment.comment);

    return (
        <ScrollableBody
            ref={screenshotRef}
            proceedButtons={
                <div style={isKiosk ? {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                } : {}}
                >
                    <TermsAndConditions
                        termsAcknowledged={termsAcknowledged ?? false}
                        setTermsAcknowledged={(val: boolean) =>
                            appointmentDispatch({
                                type: AppointmentDispatchTypes.TermsAcknowledged,
                                payload: val,
                            })
                        }
                    />
                    <GMButtonGroup
                        buttons={[
                            <CTAButton
                                styleType={'primary'}
                                onClick={() => {
                                    if (!processing) {
                                        setProcessing(true);
                                        onComplete()
                                            .finally(() => setProcessing(false));
                                    }
                                }}
                                disabled={(roBackerEnabled && roBackerAcknowledgmentRequired && !termsAcknowledged) ||
                                        (signatureIsEmpty &&
                                            ((consentToSignElectronicallyEnabled && gaveConsentToSignElectronically) || !consentToSignElectronicallyEnabled)) ||
                                        processing}
                                title={Localization.ClickToCompleteCheckIn}
                                label={Localization.CompleteCheckIn}
                                id={Localization.ClickToCompleteCheckIn}
                                ariaLabel={Localization.ClickToCompleteCheckIn}
                            />,
                        ]}
                    />
                </div>
            }
        >
            <span>{Localization.ReviewAppointment}</span>
            <GMTitle headerLevel={2} themeVariant={'primary'}>
                {Localization.AppointmentInformation}
            </GMTitle>
            <div className={reduceInfoFontSize ? css.reduceInfoFontSize : undefined}>
                <GMInfoGrid>
                    <GMInfoGridRow>
                        <GMInfoGridColumn>
                            <GMTitle headerLevel={3} themeVariant={'secondary'}>
                                <GMAppointmentName fullName />
                            </GMTitle>
                            <div data-screenshot={'phone'}>{maskPhone ? maskValue(formatAsPhoneNumber(appointment.phone)) : formatAsPhoneNumber(appointment.phone)}</div>
                            <div data-screenshot={'email'}>{maskEmail ? maskValue(appointment.email) : appointment.email}</div>
                            <div>
                                <b>
                                    {Localization.ContactBy}{' '}
                                    {appointment.contactPreference === 'phone'
                                        ? Localization.Phone
                                        : appointment.contactPreference === 'email'
                                            ? Localization.Email
                                            : appointment.contactPreference === 'text'
                                                ? Localization.Text
                                                : ''}
                                </b>
                            </div>
                        </GMInfoGridColumn>
                        <GMInfoGridColumn>
                            <GMTitle headerLevel={3} themeVariant={'secondary'}>
                                <GMCapitalize>
                                    {displayLocalizedVehicle(appointment)}
                                </GMCapitalize>
                            </GMTitle>
                            <div>{Localization.formatString(Localization.MileageParameterized, Intl.NumberFormat()
                                .format(appointment.mileage ?? 0) || '')}
                            </div>
                            <div>
                                {(vinRequired || (!!appointment.vin && appointment.vin.slice(-1) !== '~')) &&
                                        Localization.formatString(Localization.VINDisplay, maskVin ? maskValue(appointment.vin) : appointment.vin)}
                            </div>
                        </GMInfoGridColumn>
                    </GMInfoGridRow>
                    <GMInfoGridRow>
                        <GMInfoGridColumn>
                            <GMTitle headerLevel={3} themeVariant={'secondary'}>
                                {apptDate}
                            </GMTitle>
                            <h4 className={classNames(css.summaryHeader, css.customerName)}>{textCapitalize(appointment.serviceAdvisorName)}</h4>
                            <div>{transportName}</div>
                            <div>{appointment?.pickupTimeEntry}</div>
                            <div>
                                {(tagNumberRequired || !!appointment.tagNumber) && Localization.formatString(Localization.KeyTagNumberDisplay, appointment.tagNumber)}
                            </div>
                        </GMInfoGridColumn>
                        {facilityId !== '' ? (
                            <GMInfoGridColumn>
                                <GMTitle headerLevel={3} themeVariant={'secondary'}>
                                    {name}
                                </GMTitle>
                                <div>{facilityId}</div>
                            </GMInfoGridColumn>
                        ) : (
                            <></>
                        )}
                    </GMInfoGridRow>
                </GMInfoGrid>
            </div>
            <div>
                <GMTitle headerLevel={2} themeVariant={'primary'}>
                    {Localization.ServicesRequested}
                </GMTitle>
                <ServiceItemList hideTotal={!showDmsServiceLineEstimates}>{renderServices}</ServiceItemList>
            </div>
            {comments.length > 0 ? (
                <div data-print-section>
                    <GMTitle
                        headerLevel={3}
                        themeVariant={'primary'}
                        className={css.primaryReducedMarginTop}
                    >
                        {Localization.CustomerAddedComments}
                    </GMTitle>
                    <div className={css.customerComments}>
                        <div>{comments.map((c) => c.comment)}</div>
                    </div>
                </div>
            ) : null}
            {workAuthorizationEnabled && <KioskDisclaimer />}
            <Spacer />
            <div data-print-section>
                <div className={classNames({ [css.error]: signatureRequired })}>
                    <GMTitle headerLevel={2} themeVariant={'primary'}>
                        {Localization.SignBelowToAcceptService}
                    </GMTitle>
                </div>
                <SignatureComponent
                    setIsEmpty={(val: boolean) => setSignatureIsEmpty(val)}
                    processing={processing}
                    signatureRequired={signatureRequired}
                />
            </div>
            <div data-print-section>
                <Disclaimer showDisclaimerChoices />
            </div>
        </ScrollableBody>
    );
});
